import config from 'config/default';
import _ from 'lodash';
import { AWTriggers, AWTypes } from '../enums';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';
import { AugmentedAnalyses, HighlightsResponse, WordSuggestionsResponse } from '../features/augmented-writing/augmented-writing.types';
import { ProfileValues } from '../types/profile.types';
import i18n from 'i18n';

export const augmentedWritingAnalysisExternal = async (
  apiKey: string,
  profile: ProfileValues,
  lang: string,
  text: string,
  domain: AWTypes,
  awTrigger: AWTriggers
): Promise<HighlightsResponse> => {
  try {
    const response = await API_V1.post(
      'api/augmentedWriting/analysis',
      {
        lang,
        profile,
        text,
        domain,
        awTrigger,
      },
      {
        headers: {
          'x-api-key': apiKey,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
      }
    );
    return new AugmentedAnalyses({ ...response.data, coreCommit: response.headers['100w-core-commit'] });
  } catch (e) {
    Logger.trace('Error while performing Augmented writing brand analysis with temp profile');
    Logger.error(e);
    return new AugmentedAnalyses({ error: _.get(e, 'response.data', e) });
  }
};

export const getCustomerAnalysisExternal = async (text: string, apiKey: string, awTrigger: AWTriggers, domain?: AWTypes, lang?: string) => {
  try {
    const response = await API_V1.post(
      '/api/customer_intelligence/analyse',
      {
        text,
        awTrigger,
        domain,
      },
      {
        params: {
          lang: lang || i18n.languages[0],
        },
        headers: {
          'x-api-key': apiKey,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
      }
    );
    return { data: response.data, coreCommit: response.headers['100w-core-commit'] };
  } catch (e) {
    Logger.trace('Error while analyzing customer text');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const textAnalysis = async (
  profile: ProfileValues,
  lang: string,
  text: string,
  domain: string,
  awTrigger: AWTriggers,
  profiling?: boolean,
  extraParaphrases?: boolean,
  profileName?: string,
  fingerprints?: string[]
): Promise<WordSuggestionsResponse> => {
  try {
    const tempLang = config.supportedLanguages.includes(lang) ? lang : 'en';
    const response = await API_V1.post('/augmented-writing/suggestions', {
      lang: tempLang,
      profile,
      text,
      domain,
      awTrigger,
      profiling,
      extraParaphrases,
      profileName,
      ignoredFingerprints: fingerprints,
    });
    return new AugmentedAnalyses({ ...response.data, coreCommit: response.headers['100w-core-commit'] });
  } catch (e) {
    Logger.trace('Error while performing Augmented Writing analysis');
    Logger.error(e);
    return new AugmentedAnalyses({ error: _.get(e, 'response.data', e) });
  }
};

export const textAnalysisExternal = async (
  apiKey: string,
  text: string,
  profile: ProfileValues,
  domain: AWTypes,
  awTrigger: AWTriggers,
  ignoredFingerprints: string[]
): Promise<WordSuggestionsResponse> => {
  try {
    const tempLang = config.supportedLanguages.includes(i18n.languages[0]) ? i18n.languages[0] : 'en';
    const response = await API_V1.post(
      '/api/augmentedWriting',
      {
        text,
        lang: tempLang,
        profile,
        domain,
        awTrigger,
        ignoredFingerprints,
      },
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );
    return new AugmentedAnalyses({ ...response.data, coreCommit: response.headers['100w-core-commit'] });
  } catch (e) {
    Logger.trace('Error while analyzing aw external!');
    Logger.error(e);
    return new AugmentedAnalyses({ error: _.get(e, 'response.data', e) });
  }
};

export const quickOptimization = async (profile: ProfileValues, lang: string, text: string, domain: string) => {
  try {
    const tempLang = config.supportedLanguages.includes(lang) ? lang : 'en';
    const response = await API_V1.post('/augmented-writing/quick-optimize', {
      lang: tempLang,
      profile,
      text,
      domain,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while Augmented Writing quick optimization analysis');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const autoFormat = async (text: string, includeEmoji?: boolean) => {
  try {
    const response = await API_V1.post('/misc/format-text', {
      text,
      includeEmoji: !_.isNil(includeEmoji) ? includeEmoji : false,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while auto formatting text!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const saveIgnoredSuggestions = async (fingerprints: string[]) => {
  try {
    const response = await API_V1.post('/misc/ignored-suggestions', { fingerprints });

    return response.data;
  } catch (e) {
    Logger.trace('Error while saving ignored suggestions from augmented writing');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const saveIgnoredSuggestionsExternal = async (apiKey: string, fingerprints: string[]) => {
  try {
    const response = await API_V1.post(
      '/api/ignore/saveIgnoredSuggestions',
      { fingerprints },
      {
        headers: {
          'x-api-key': apiKey,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while saving ignored suggestions from augmented writing');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
